import {Form, Formik, FormikProps} from 'formik';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Button,
    DataWrapper,
    TextAreaFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {dataUtils} from 'utils';

import {PromtsFields} from '../types';

const FIELDS = {
    KNOWLEDGE_BASE: 'knowledgeBase',
    BOT_INSTRUCTION: 'botInstruction',
    SUGGESTION_INSTRUCTION: 'suggestionInstruction',
};

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<PromtsFields>;

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CHAT_GPT.PROMTS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, initialValues, submitForm, values
}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-mt-2' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-ts-h5-bold'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-text-default tw-mt-4'>
                            {t(T_KEYS.DESCRIPTION)}
                        </div>
                        <TextAreaFormik
                            name={FIELDS.KNOWLEDGE_BASE}
                            label={t(T_KEYS.LABELS.KNOWLEDGE_BASE)}
                            rows={6}
                            spacing='top'
                        />
                        <TextAreaFormik
                            name={FIELDS.BOT_INSTRUCTION}
                            label={t(T_KEYS.LABELS.BOT_INSTRUCTION)}
                            rows={6}
                            spacing='top'
                        />
                        <TextAreaFormik
                            name={FIELDS.SUGGESTION_INSTRUCTION}
                            label={t(T_KEYS.LABELS.SUGGESTION_INSTRUCTION)}
                            rows={6}
                            spacing='top'
                        />
                        <div className='tw-text-right tw-my-4'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type Props = {
    loading: boolean;
    initialValues: PromtsFields;
    onSubmit: (values: PromtsFields) => void;
};

export const Promts: React.FC<Props> = ({
    loading, initialValues, onSubmit
}) =>
    (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    loading={loading}
                />
            )}
        </Formik>
    )
;